import { useGetCampaignsAdminHttp } from "../hooks/http/admin/campaigns/get-campaigns.admin.http";
import {
    ActiveCampaignBadge,
    ClosedBadge,
    ReelsBadge,
    ShortsBadge,
    VideoBadge,
} from "./badges.components";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "./ui/accordion";
import { Separator } from "./ui/separator";
import { handleHttpError } from "../utils/utils";
import { useToast } from "../hooks/use-toast";
import { useEffect, useState } from "react";
import Spinner from "./spinner.components";
import { DateTime } from "luxon";
import type { IPagination } from "../types/models/pagination.models.types";
import PaginationComponent from "./pagination.components";
import {
    SocialPlatforms,
    SocialSliderMenuSortOption,
    SortOrder,
    type ISocial,
} from "../types/models/social.models.types";
import {
    CampaignStatus,
    SocialPostType,
    type ICampaign,
} from "../types/models/campaign.models.types";
import {
    Select,
    SelectContent,
    SelectGroup,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "./ui/select";

export default function UserParticipationHistory({
    social,
}: {
    social: ISocial;
}) {
    const { execute: getParticipationHistoryHttp, fetching: fetchingParticipationHistory } =
        useGetCampaignsAdminHttp();
    const [campaigns, setCampaigns] = useState<ICampaign[]>([]);
    const [paginationData, setPaginationData] = useState<IPagination>();
    const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.DESC);
    const [selectedSort,setSelectedSort] = useState<SocialSliderMenuSortOption>(SocialSliderMenuSortOption.NEWEST);
    const { toast } = useToast();

    async function fetchParticipationHistory(pageNo: number) {
        if (!social.id) {
            return;
        }

        const response = await getParticipationHistoryHttp({
            body: null,
            queryParams: {
                socialId: social.id,
                page: pageNo,
                sortOrder,
            },
        });
        if (!response.success) {
            return handleHttpError(response, toast);
        }
        if (pageNo === 1) {
            setCampaigns(response.data.campaigns);
        } else {
            setCampaigns((prev) => [...prev, ...response.data.campaigns]);
        }
        setPaginationData(response.data.pagination);
    }

    function handleSortChange(value: string) {
        const next = value as SocialSliderMenuSortOption;
        if (next === selectedSort){
            return;
        }
        setSelectedSort(next);
        setCampaigns([]);
        setSortOrder(value === SocialSliderMenuSortOption.OLDEST ? SortOrder.ASC : SortOrder.DESC);
    }

    function getCampaignStatusBadge(campaign: ICampaign) {
        if (campaign.campaignStatus.status === CampaignStatus.live) {
            return <ActiveCampaignBadge />;
        }
        if (campaign.campaignStatus.status === CampaignStatus.ended) {
            return <ClosedBadge />;
        }
        return null;
    }

    function getCampaignTypeBadge(campaign: ICampaign) {
        if (campaign.socialPlatform === SocialPlatforms.instagram) {
            return <ReelsBadge />;
        }
        if (campaign.socialPlatform === SocialPlatforms.youtube) {
            if (campaign.socialPostType === SocialPostType.YOUTUBE_SHORT) {
                return <ShortsBadge />;
            }
            if (campaign.socialPostType === SocialPostType.YOUTUBE_VIDEO) {
                return <VideoBadge />;
            }
        }
        return null;
    }

    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
        fetchParticipationHistory(1);
    }, [social.id, sortOrder]);

    if (campaigns.length <= 0 && fetchingParticipationHistory) {
        return (
            <div className="flex flex-col justify-center items-center h-96">
                <Spinner width={20} />
            </div>
        );
    }

    return (
        <div className="flex flex-col px-2 w-full">
            <div className="flex items-center justify-between w-full px-4 py-3">
               
                    <h1 className="text-secondary ">Campaigns</h1>

                    <Select
                        onValueChange={(e) => handleSortChange(e)}
                        value={selectedSort}
                    >
                        <SelectTrigger className="gap-2 py-6 w-fit">
                            <span className="text-secondary">Sort by:</span>
                            <SelectValue className="" />
                        </SelectTrigger>
                        <SelectContent className="bg-transparent">
                            <SelectGroup className="p-1 font-semibold">
                                <SelectItem value={SocialSliderMenuSortOption.NEWEST}>Newest</SelectItem>
                                <div className="border-t border-border" />
                                <SelectItem value={SocialSliderMenuSortOption.OLDEST}>Oldest</SelectItem>
                            </SelectGroup>
                        </SelectContent>
                    </Select>

            </div>
            <div className="flex flex-col gap-5 pt-5 w-full">
                {campaigns.map((campaign) => (
                    <Accordion
                        key={campaign.id}
                        className="bg-tertiarybuttonbg px-4 rounded-lg w-full"
                        type="single"
                        collapsible
                    >
                        <AccordionItem value={campaign.id}>
                            <AccordionTrigger className="flex flex-row gap-2">
                                <div className="flex items-center w-full">
                                    <p className="font-semibold text-base truncate max-w-[250px]">
                                        {campaign.name}
                                    </p>
                                </div>
                            </AccordionTrigger>
                            <AccordionContent>
                                <Separator />
                                <div className="flex flex-col gap-4 pt-3">
                                    <div className="flex justify-between items-center">
                                        <div className="flex flex-col flex-1 text-start">
                                            <span className="text-sm text-secondary">Status</span>
                                            <div className="flex items-center gap-2 pt-1">
                                                {getCampaignStatusBadge(campaign)}
                                            </div>
                                        </div>
                                        <div className="flex flex-col flex-1 pl-16 text-start">
                                            <span className="text-sm text-secondary">Type</span>
                                            <div className="flex items-center gap-2 pt-1">
                                                {getCampaignTypeBadge(campaign)}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <div className="flex flex-col flex-1 text-start">
                                            <span className="text-sm text-secondary">Start Date</span>
                                            <p className="text-base font-semibold pt-1">
                                                {campaign.startDate
                                                    ? DateTime.fromISO(campaign.startDate.toString()).setZone("local").toFormat("dd-MM-yy")
                                                    : "-"}
                                            </p>
                                        </div>
                                        <div className="flex flex-col flex-1 pl-16 text-start">
                                            <span className="text-sm text-secondary">End Date</span>
                                            <p className="text-base font-semibold pt-1">
                                                {campaign.endDate
                                                    ? DateTime.fromISO(campaign.endDate.toString()).setZone("local").toFormat("dd-MM-yy")
                                                    : "-"}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </AccordionContent>
                        </AccordionItem>
                    </Accordion>
                ))}
                {campaigns.length === 0 && (
                    <div className="flex justify-center items-center py-10">
                        <p className="text-secondary text-sm">No campaigns found</p>
                    </div>
                )}
            </div>
            {paginationData && (
                <PaginationComponent
                    fetching={fetchingParticipationHistory}
                    paginationData={paginationData}
                    onClick={() => fetchParticipationHistory(paginationData.page + 1)}
                />
            )}
        </div>
    );
}
